import React, { useState } from "react";
import { Box, Typography, useTheme } from "@mui/material";
import { Plus, X } from "lucide-react";
import MediaLibraryPickerCst from "@components/MediaLibraryPickerCst";
import { MediaType } from "src/interfaces/Media";
import { AssociationItem } from "../../../types";
import { PERMISSIONS, usePermissions } from "@utils/permissions";
import { useWizardReadOnly } from "@components/WizardCst";

interface AssociationFieldPlaceholderProps {
  label: string;
  required?: boolean;
  value: AssociationItem | null;
  onChange: (next: AssociationItem | null) => void;
  addLabel: string;
  emptyLabel: string;
  error?: boolean;
  errorMessage?: string;
  mediaType?: MediaType;
  pickerTitle?: string;
  onAddClick?: () => void;
  disabled?: boolean;
}

const AssociationFieldPlaceholder: React.FC<
  AssociationFieldPlaceholderProps
> = ({
  label,
  required,
  value,
  onChange,
  addLabel,
  emptyLabel,
  error,
  errorMessage,
  mediaType,
  pickerTitle,
  onAddClick,
  disabled = false,
}) => {
  const theme = useTheme();
  const [pickerOpen, setPickerOpen] = useState(false);
  const { can } = usePermissions();
  const readOnly = useWizardReadOnly();
  const canManageMedia =
    !mediaType || can([PERMISSIONS.media.read, PERMISSIONS.media.write]);
  const addDisabled = readOnly || disabled || !canManageMedia;

  const handleAddClick = () => {
    if (addDisabled) return;
    if (onAddClick) {
      onAddClick();
      return;
    }
    if (mediaType) {
      setPickerOpen(true);
      return;
    }
    // TODO: fallback mock per associazioni senza picker dedicato.
    const fake: AssociationItem = {
      id: Date.now(),
      label: `${addLabel} ${new Date().toLocaleTimeString()}`,
    };
    onChange(fake);
  };

  const handleRemove = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange(null);
  };

  const borderColor = error ? theme.palette.error.main : theme.palette.divider;

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 1,
        }}
      >
        <Typography
          sx={{
            fontSize: "13px",
            fontWeight: 500,
            color: theme.palette.text.primary,
          }}
        >
          {label}
          {required && (
            <Box component="span" sx={{ color: theme.palette.error.main }}>
              {" *"}
            </Box>
          )}
        </Typography>
        <Box
          onClick={handleAddClick}
          aria-disabled={addDisabled}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "4px",
            cursor: addDisabled ? "not-allowed" : "pointer",
            color: addDisabled
              ? theme.palette.text.disabled
              : theme.palette.primary.main,
            fontSize: "13px",
            fontWeight: 600,
            "&:hover": addDisabled ? {} : { textDecoration: "underline" },
          }}
        >
          {addLabel}
          <Plus size={14} />
        </Box>
      </Box>

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
          padding: "12px 16px",
          minHeight: "44px",
          borderRadius: "8px",
          border: `1px solid ${borderColor}`,
          backgroundColor: theme.palette.background.paper,
          color: value
            ? theme.palette.text.primary
            : theme.palette.text.tertiary,
        }}
      >
        <Typography sx={{ fontSize: "13px" }}>
          {value ? value.label || `#${value.id}` : emptyLabel}
        </Typography>
        {value && !readOnly && !disabled && canManageMedia && (
          <Box
            onClick={handleRemove}
            sx={{
              display: "flex",
              cursor: "pointer",
              color: theme.palette.text.tertiary,
              "&:hover": { color: theme.palette.error.main },
            }}
          >
            <X size={16} />
          </Box>
        )}
      </Box>

      {error && errorMessage && (
        <Typography
          sx={{
            fontSize: "12px",
            color: theme.palette.error.main,
            mt: 0.5,
            ml: 1,
          }}
        >
          {errorMessage}
        </Typography>
      )}

      {mediaType && (
        <MediaLibraryPickerCst
          open={pickerOpen}
          onClose={() => setPickerOpen(false)}
          title={pickerTitle || label}
          mediaType={mediaType}
          multiple={false}
          initialSelection={
            value ? { id: Number(value.id), label: value.label || "" } : null
          }
          onConfirm={(selection) => onChange(selection)}
        />
      )}
    </Box>
  );
};

export default AssociationFieldPlaceholder;
